import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createStudentProposal,
  getMyStudentProposals,
  searchTutors,
  type SearchTutorsBody,
} from 'api/v1/requests/bulletins';
import { chatKeys } from 'modules/ChatV2/api/queries';

export type StudentTutorSortPreset = 'praised' | 'rating' | 'price' | null;

export const STUDENT_TUTOR_SORT_PRESETS: {
  id: Exclude<StudentTutorSortPreset, null>;
  label: string;
  body: Pick<SearchTutorsBody, 'rating_min' | 'by_order'>;
}[] = [
  {
    id: 'praised',
    label: 'Очень хвалят',
    body: { rating_min: 4.0, by_order: { reviews: true } },
  },
  {
    id: 'rating',
    label: 'Высокий рейтинг',
    body: { rating_min: 4.0, by_order: { rating: true } },
  },
  {
    id: 'price',
    label: 'Меньше ценник',
    body: { by_order: { price: false } },
  },
];

export const STUDENT_TUTOR_PAGE_SIZE = 5;

export const useSearchTutorsByBulletin = (
  bulletinId: number,
  sortPreset: StudentTutorSortPreset = null,
  page = 0,
) => {
  const preset = STUDENT_TUTOR_SORT_PRESETS.find((p) => p.id === sortPreset);
  const offset = Math.max(0, page) * STUDENT_TUTOR_PAGE_SIZE;

  return useQuery({
    queryKey: ['searchTutors', bulletinId, sortPreset, page],
    queryFn: () =>
      searchTutors({
        bulletin_id: bulletinId,
        limit: STUDENT_TUTOR_PAGE_SIZE,
        offset,
        mode: 'any',
        ...(preset?.body ?? {}),
      }),
    enabled: Number.isFinite(bulletinId) && bulletinId > 0,
  });
};

export const useMyStudentProposals = () =>
  useQuery({
    queryKey: ['studentProposals', 'mine'],
    queryFn: getMyStudentProposals,
  });

export const useCreateStudentProposal = (bulletinId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { tutor_profile_id: number; message?: string }) =>
      createStudentProposal(bulletinId, vars),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['studentProposals', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['searchTutors', bulletinId] });
      void queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      void queryClient.invalidateQueries({ queryKey: ['student_chat_bulletin_threads'] });
      void queryClient.invalidateQueries({ queryKey: ['student_bulletin_tutors'] });
    },
  });
};
